import { useQuery } from '@tanstack/react-query';
import { uploadService } from '../services/uploadService';
import { IMAGE_KEYS } from './queryKeys';

/**
 * Returns whether a profileImage value is an S3 key (needs presigned URL)
 * vs an external URL (can be used directly).
 */
const isS3Key = (value: string): boolean => !value.startsWith('http');

/**
 * Resolves a profileImage value to a displayable URL.
 * - S3 keys (e.g. "profile-images/member-123/uuid.jpg") → fetches a presigned GET URL
 * - External URLs (e.g. "https://example.com/photo.jpg") → returned as-is
 * - Null/undefined → returns null
 *
 * Presigned URLs are cached for 50 minutes (they expire after 60 minutes).
 */
export function useImageUrl(profileImage: string | null | undefined): string | null {
  const shouldFetch = !!profileImage && isS3Key(profileImage);

  const { data } = useQuery({
    queryKey: IMAGE_KEYS.url(profileImage || ''),
    queryFn: () => uploadService.getImageUrl(profileImage!),
    enabled: shouldFetch,
    staleTime: 50 * 60 * 1000, // 50 minutes (presigned URLs expire in 60)
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  if (!profileImage) return null;
  if (!isS3Key(profileImage)) return profileImage;
  return data ?? null;
}
